import { lastAppPage } from './supportTicket';

/*
 * In-app links from the Help Center and help answers.
 *
 * Budget + Forecast reads its URL only on mount, so a link into it carries
 * `forecastJump` history state (App.js ForecastRoute remounts on it) — the
 * link then works even when the workspace is already open.
 */
export function linkState(to) {
  return String(to || '').startsWith('/forecast-v3') ? { forecastJump: Date.now() } : undefined;
}

/** Where a link from Help or the assistant goes when its page is gated for this user. */
export const noAccessPath = (to) => `/no-access?to=${encodeURIComponent(to)}`;

/**
 * Link target + history state for an in-app link from Help or the assistant.
 * `canOpen` is useCanOpen() (navAccess): a page this user can't open goes to
 * the "you don't have access" page instead of a silent redirect. `fromHelp`
 * lets the route guards make the same call when the check couldn't.
 */
export function helpLinkTarget(to, canOpen) {
  if (!String(to || '').startsWith('/') || to.startsWith('/help')) return { to, state: undefined };
  const access = canOpen ? canOpen(to) : { allowed: true };
  if (!access.allowed) return { to: noAccessPath(to), state: { fromHelp: true } };
  return { to, state: { fromHelp: true, ...(linkState(to) || {}) } };
}

/**
 * A guide's plain link to the page the user just came from keeps their
 * selection: `/forecast-v3` from a Help Center article reopens the property,
 * version and account they had open; the link's own params still win.
 */
export function withCurrentSelection(to) {
  const last = lastAppPage();
  if (!last || !String(to || '').startsWith('/') || to.startsWith('/help')) return to;
  const [path, query = ''] = to.split('?');
  const [lastPath, lastQuery = ''] = last.split('?');
  if (path !== lastPath || !lastQuery) return to;
  const merged = new URLSearchParams(lastQuery);
  new URLSearchParams(query).forEach((value, key) => merged.set(key, value));
  return `${path}?${merged.toString()}`;
}
